import { Input } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { inventoryFieldProps } from '../../utils/inventoryFieldStyles';

interface QuantityInputFieldProps {
    value: number | null;
    onChange: (value: number | null) => void;
    size?: 'sm' | 'md';
    isDisabled?: boolean;
    variant?: 'outline' | 'unstyled';
    fontSize?: string;
    fontWeight?: string;
    color?: string;
    autoFocus?: boolean;
    onBlur?: () => void;
    /**
     * When true, empty input commits `null` instead of `0`.
     * Use for optional reorder/target when restockAlert is false.
     */
    allowEmpty?: boolean;
}

function displayFromValue(v: number | null): string {
    if (v == null || v === 0) return '';
    return String(v);
}

const QuantityInputField = ({
    value,
    onChange,
    size = 'sm',
    isDisabled = false,
    variant = 'outline',
    fontSize,
    fontWeight,
    color,
    autoFocus = false,
    onBlur,
    allowEmpty = false,
}: QuantityInputFieldProps) => {
    const [text, setText] = useState('');
    const [focused, setFocused] = useState(false);

    useEffect(() => {
        if (!focused) {
            setText(displayFromValue(value));
        }
    }, [value, focused]);

    const commit = (raw: string) => {
        const trimmed = raw.trim();
        if (trimmed === '') {
            onChange(allowEmpty ? null : 0);
            return;
        }
        const n = parseInt(trimmed, 10);
        if (!Number.isNaN(n) && n >= 0) {
            onChange(n);
        }
    };

    const displayValue = focused ? text : displayFromValue(value);

    return (
        <Input
            size={size}
            variant={variant}
            fontSize={fontSize}
            fontWeight={fontWeight}
            color={color}
            autoFocus={autoFocus}
            type="number"
            min={0}
            placeholder="0"
            value={displayValue}
            onChange={(e) => setText(e.target.value)}
            onFocus={() => {
                setFocused(true);
                setText(displayFromValue(value));
            }}
            onBlur={() => {
                setFocused(false);
                commit(text);
                onBlur?.();
            }}
            onKeyDown={(e) => {
                if (e.key === 'Enter') {
                    (e.target as HTMLInputElement).blur();
                }
            }}
            isDisabled={isDisabled}
            {...(variant === 'outline' ? inventoryFieldProps : {})}
        />
    );
};

export default QuantityInputField;
